// context/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import authService from '../services/authService';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUserFromStorage = async () => {
      try {
        const storedUser = localStorage.getItem('user');
        const storedUserId = localStorage.getItem('userId');
        
        console.log('🔍 AuthContext - Vérification localStorage:', {
          storedUser: storedUser ? 'présent' : 'absent',
          storedUserId
        });
        
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          
          // Optionnel : Vérifier avec le serveur que l'utilisateur est toujours valide
          try {
            const response = await authService.verifySession(userData.id);
            if (response.success) {
              setUser(userData);
              console.log('✅ Utilisateur restauré depuis localStorage:', userData);
            } else {
              // Session expirée, nettoyer
              localStorage.removeItem('user');
              localStorage.removeItem('userId');
              console.log('⚠️ Session expirée, utilisateur déconnecté');
            }
          } catch (error) {
            // En cas d'erreur réseau, on garde l'utilisateur en cache
            setUser(userData);
            console.log('⚠️ Utilisateur restauré (mode hors ligne):', userData);
          }
        }
      } catch (error) {
        console.error('❌ Erreur lecture localStorage:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('userId');
      } finally {
        setLoading(false);
      }
    };

    loadUserFromStorage();
  }, []);

  const login = async (email, password) => {
    try {
      console.log('🔐 AuthContext.login - Tentative pour:', email);
      
      const result = await authService.login(email, password);
      
      if (result.success && result.user) {
        // Stocker dans localStorage
        localStorage.setItem('user', JSON.stringify(result.user));
        localStorage.setItem('userId', result.user.id);
        
        setUser(result.user);
        toast.success('Connexion réussie ! 🔥');
        
        return { 
          success: true, 
          user: result.user
        };
      } else {
        toast.error(result.error || 'Erreur de connexion');
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('❌ AuthContext - Erreur:', error);
      toast.error('Erreur de connexion au serveur');
      return { success: false, error: 'Erreur réseau' };
    }
  };

  const logout = () => {
    // Nettoyer le localStorage
    localStorage.removeItem('user');
    localStorage.removeItem('userId');
    localStorage.removeItem('token');
    
    setUser(null);
    toast.success('À bientôt !');
    
    // Rediriger vers login
    window.location.href = '/login';
  };

  /**
   * Mettre à jour les données utilisateur dans le contexte et localStorage
   * @param {Object} updatedData - Nouvelles données à fusionner avec l'utilisateur existant
   */
  const updateUser = (updatedData) => {
    if (!user) {
      console.warn('⚠️ Tentative de mise à jour alors qu\'aucun utilisateur n\'est connecté');
      return;
    }

    const updatedUser = { 
      ...user, 
      ...updatedData,
      // S'assurer que l'ID n'est pas perdu
      id: user.id 
    };
    
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
    
    console.log('✅ Utilisateur mis à jour:', updatedUser);
  };

  /**
   * Rafraîchir les données utilisateur depuis le serveur
   */
  const refreshUser = async () => {
    if (!user?.id) return;

    try {
      const response = await authService.getUserProfile(user.id);
      if (response.success) {
        const updatedUser = { ...user, ...response.user };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        console.log('✅ Utilisateur rafraîchi:', updatedUser);
      }
    } catch (error) {
      console.error('❌ Erreur lors du rafraîchissement utilisateur:', error);
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    updateUser,
    refreshUser,
    isAuthenticated: !!user,
    userId: user?.id,
    userPseudo: user?.pseudo,
    userAvatar: user?.avatar,
    userEmail: user?.email,
    isPremium: user?.is_premium || false,
    isVerified: user?.is_verified || false
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};